/** @jsx jsx */
import {jsx} from 'theme-ui'
import {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

interface IDonationMultiPriceBlock {
  defaultAmount: number
  userInput: null | 'custom' | 'fixed'
  amount: number | null
  onAmountChange: (
    amount: number | null,
    userInput: null | 'custom' | 'fixed',
  ) => void
  amounts: number[]
  priceTag: string
}

function initAmount(
  userInput: string | null,
  defaultAmount: number,
  amount: number | null,
  amounts: number[],
): {fixed: number | null; custom: string} {
  let fixed: number | null = null
  let custom = ''

  if (amount && amounts.includes(amount)) {
    fixed = amount
  } else if (userInput === null || userInput === 'fixed') {
    fixed = defaultAmount
  } else if (userInput === 'custom') {
    custom = String(amount)
  }

  return {fixed, custom}
}

const DonationMultiPriceBlock: React.FC<IDonationMultiPriceBlock> = ({
  amount,
  userInput,
  defaultAmount,
  onAmountChange,
  priceTag,
  amounts,
}) => {
  const {t} = useTranslation()
  const [temporaryAmount, setTemporaryAmount] = useState<{
    fixed: number | null
    custom: string
  }>(initAmount(userInput, defaultAmount, amount, amounts))

  useEffect(() => {
    if (temporaryAmount.custom) {
      const newAmount = parseInt(temporaryAmount.custom, 10)
      if (!Number.isNaN(newAmount)) {
        onAmountChange(newAmount, 'custom')
      }
    } else if (temporaryAmount.fixed !== null) {
      onAmountChange(temporaryAmount.fixed, 'fixed')
    } else {
      onAmountChange(null, null)
    }
  }, [temporaryAmount.fixed, temporaryAmount.custom, onAmountChange])

  return (
    <div
      sx={{
        padding: '16px 12px',
        display: 'grid',
        gridTemplateColumns: ['1fr 1fr 1fr', , '94px 94px 94px'],
        gridTemplateRows: ['52px 52px', , '46px 46px'],
      }}
      style={{
        gridColumnGap: 16,
        gridRowGap: 14,
      }}
    >
      {amounts.map((amount) => {
        const isSelected = temporaryAmount.fixed === amount
        const handleClick = () => {
          setTemporaryAmount({
            fixed: amount,
            custom: '',
          })
        }
        return (
          <button
            type="button"
            key={amount}
            onClick={handleClick}
            sx={{
              cursor: 'pointer',
              color: isSelected ? '#fff' : '#566366',
              borderRadius: 4,
              border: 'none',
              outline: 0,
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.07)',
              backgroundColor: isSelected ? 'primary' : '#fff',
            }}
          >
            <span
              sx={{
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {amount}
            </span>
            <span
              sx={{
                fontSize: 8,
                ml: 2,
              }}
            >
              {priceTag}
            </span>
          </button>
        )
      })}
      <div sx={{gridColumnEnd: 'span 2'}}>
        <input
          type="number"
          sx={{
            width: '100%',
            height: '100%',
            border: `1px solid ${
              temporaryAmount.custom ? '#055064' : '#DEE3E3'
            }`,
            borderRadius: 4,
            padding: '15px 12px',
            fontSize: 13,
            outline: 0,
            '::placeholder': {
              color: '#717A7B',
            },
            '&:focus': {
              border: '1px solid #055064',
            },
          }}
          placeholder={t('Amount') + ' ' + priceTag}
          onFocus={() =>
            setTemporaryAmount({
              ...temporaryAmount,
              fixed: null,
            })
          }
          value={temporaryAmount.custom}
          onChange={(event) => {
            const value = event.target.value
            setTemporaryAmount({
              fixed: null,
              custom: value,
            })
          }}
        />
      </div>
    </div>
  )
}

export default DonationMultiPriceBlock
